import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Fab,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Notifications,
  Schedule,
  CheckCircle,
  Snooze,
  Repeat,
  Warning,
  Search,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import dayjs from 'dayjs'
import { reminderService } from '../../services/reminderService'
import { Reminder, ReminderFormData } from '../../types'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ConfirmDialog from '../../components/Common/ConfirmDialog'
import { useAuth } from '../../contexts/AuthContext'

const reminderSchema = yup.object({
  title: yup.string().required('Title is required').min(2, 'Title must be at least 2 characters'),
  description: yup.string(),
  reminderDate: yup.string().required('Reminder date is required'),
  isRecurring: yup.boolean(),
  recurrenceType: yup.string().oneOf(['daily', 'weekly', 'monthly', 'yearly']).when('isRecurring', {
    is: true,
    then: (schema) => schema.required('Recurrence type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  recurrenceInterval: yup.number().when('isRecurring', {
    is: true,
    then: (schema) => schema.required('Recurrence interval is required').min(1, 'Interval must be at least 1'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const RemindersPage: React.FC = () => {
  const { user } = useAuth()
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'completed' | 'overdue'>('all')
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [reminderToDelete, setReminderToDelete] = useState<Reminder | null>(null)
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false)
  const [reminderToSnooze, setReminderToSnooze] = useState<Reminder | null>(null)
  const [snoozeHours, setSnoozeHours] = useState(1)
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(null)

  // Form
  const form = useForm<ReminderFormData>({
    resolver: yupResolver(reminderSchema),
    defaultValues: {
      title: '',
      description: '',
      reminderDate: new Date().toISOString(),
      isRecurring: false,
      recurrenceType: 'daily',
      recurrenceInterval: 1,
    }
  })

  const fetchReminders = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await reminderService.getReminders()
      setReminders(data.data || []) // Handle paginated response
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && localStorage.getItem('auth_token')) {
      console.log('RemindersPage: User authenticated, fetching reminders')
      fetchReminders()
    } else {
      console.log('RemindersPage: User not authenticated, skipping fetch')
    }
  }, [user])

  const handleSubmit = async (data: ReminderFormData) => {
    try {
      // Ensure reminderDate is a Date object before calling toISOString
      let reminderDate: Date
      if (typeof data.reminderDate === 'string') {
        reminderDate = new Date(data.reminderDate)
      } else {
        reminderDate = new Date()
      }
      
      if (editingReminder) {
        const updateData: Partial<ReminderFormData> = {
          title: data.title,
          description: data.description,
          reminderDate: reminderDate.toISOString(),
          isRecurring: data.isRecurring,
          recurrenceType: data.recurrenceType as 'daily' | 'weekly' | 'monthly' | 'yearly',
          recurrenceInterval: data.recurrenceInterval,
        }
        const updated = await reminderService.updateReminder(editingReminder.id, updateData)
        setReminders(prev => prev.map(reminder => reminder.id === editingReminder.id ? updated : reminder))
      } else {
        const createData: ReminderFormData = {
          title: data.title,
          description: data.description,
          reminderDate: reminderDate.toISOString(),
          isRecurring: data.isRecurring,
          recurrenceType: data.recurrenceType as 'daily' | 'weekly' | 'monthly' | 'yearly',
          recurrenceInterval: data.recurrenceInterval,
        }
        const newReminder = await reminderService.createReminder(createData)
        setReminders(prev => [...prev, newReminder])
      }
      
      setDialogOpen(false)
      setEditingReminder(null)
      form.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save reminder')
    }
  }

  const handleComplete = async (reminder: Reminder) => {
    try {
      await reminderService.completeReminder(reminder.id)
      setReminders(prev => prev.map(r => r.id === reminder.id ? { ...r, isCompleted: true, completedAt: new Date().toISOString() } : r))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete reminder')
    }
  }

  const handleSnooze = async () => {
    if (!reminderToSnooze) return
    
    try {
      const snoozeUntil = new Date(Date.now() + snoozeHours * 60 * 60 * 1000)
      await reminderService.snoozeReminder(reminderToSnooze.id, snoozeUntil.toISOString())
      setReminders(prev => prev.map(r => r.id === reminderToSnooze.id ? { ...r, snoozedUntil: snoozeUntil.toISOString() } : r))
      setSnoozeDialogOpen(false)
      setReminderToSnooze(null)
      setSnoozeHours(1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to snooze reminder')
    }
  }

  const handleDelete = async () => {
    if (!reminderToDelete) return
    
    try {
      await reminderService.deleteReminder(reminderToDelete.id)
      setReminders(prev => prev.filter(r => r.id !== reminderToDelete.id))
      setDeleteConfirmOpen(false)
      setReminderToDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete reminder')
    }
  }

  const openEditDialog = (reminder: Reminder) => {
    setEditingReminder(reminder)
    form.reset({
      title: reminder.title,
      description: reminder.description || '',
      reminderDate: new Date(reminder.reminderDate).toISOString(),
      isRecurring: reminder.isRecurring,
      recurrenceType: reminder.recurrenceType || 'daily',
      recurrenceInterval: reminder.recurrenceInterval || 1,
    })
    setDialogOpen(true)
    setAnchorEl(null)
  }

  const openDeleteConfirm = (reminder: Reminder) => {
    setReminderToDelete(reminder)
    setDeleteConfirmOpen(true)
    setAnchorEl(null)
  }

  const openSnoozeDialog = (reminder: Reminder) => {
    setReminderToSnooze(reminder)
    setSnoozeDialogOpen(true)
    setAnchorEl(null)
  }

  const getFilteredReminders = () => {
    let filtered = reminders

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(reminder =>
        reminder.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (reminder.description && reminder.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Apply status filter
    const now = new Date()
    switch (filterStatus) {
      case 'pending':
        filtered = filtered.filter(r => !r.isCompleted && new Date(r.reminderDate) > now)
        break
      case 'completed':
        filtered = filtered.filter(r => r.isCompleted)
        break
      case 'overdue':
        filtered = filtered.filter(r => !r.isCompleted && new Date(r.reminderDate) <= now)
        break
    }

    return filtered.sort((a, b) => new Date(a.reminderDate).getTime() - new Date(b.reminderDate).getTime())
  }

  const getReminderStatus = (reminder: Reminder) => {
    if (reminder.isCompleted) return 'completed'
    if (reminder.snoozedUntil && new Date(reminder.snoozedUntil) > new Date()) return 'snoozed'
    if (new Date(reminder.reminderDate) <= new Date()) return 'overdue'
    return 'pending'
  }

  const getReminderIcon = (reminder: Reminder) => {
    const status = getReminderStatus(reminder)
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main' }} />
      case 'overdue':
        return <Warning sx={{ color: 'error.main' }} />
      case 'snoozed':
        return <Snooze sx={{ color: 'warning.main' }} />
      default:
        return reminder.isRecurring ? <Repeat sx={{ color: 'info.main' }} /> : <Schedule sx={{ color: 'primary.main' }} />
    }
  }

  const getStatusCounts = () => {
    const now = new Date()
    return {
      all: reminders.length,
      pending: reminders.filter(r => !r.isCompleted && new Date(r.reminderDate) > now).length,
      completed: reminders.filter(r => r.isCompleted).length,
      overdue: reminders.filter(r => !r.isCompleted && new Date(r.reminderDate) <= now).length,
    }
  }

  const filteredReminders = getFilteredReminders()
  const statusCounts = getStatusCounts()

  if (loading) {
    return (
      <Container maxWidth="lg">
        <LoadingSpinner />
      </Container>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Reminders
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Stay on top of your financial tasks and deadlines
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {statusCounts.all}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {statusCounts.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main">
                  {statusCounts.overdue}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overdue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {statusCounts.completed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search reminders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All ({statusCounts.all})</MenuItem>
              <MenuItem value="pending">Pending ({statusCounts.pending})</MenuItem>
              <MenuItem value="overdue">Overdue ({statusCounts.overdue})</MenuItem>
              <MenuItem value="completed">Completed ({statusCounts.completed})</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Add Reminder
          </Button>
        </Box>

        {/* Reminders List */}
        {filteredReminders.length > 0 ? (
          <Paper>
            <List>
              {filteredReminders.map((reminder, index) => {
                const status = getReminderStatus(reminder)
                return (
                  <React.Fragment key={reminder.id}>
                    <ListItem
                      sx={{
                        opacity: reminder.isCompleted ? 0.6 : 1,
                        bgcolor: status === 'overdue' ? 'error.light' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemIcon>
                        {getReminderIcon(reminder)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              variant="body1"
                              sx={{
                                textDecoration: reminder.isCompleted ? 'line-through' : 'none',
                                fontWeight: status === 'overdue' ? 'bold' : 'normal'
                              }}
                            >
                              {reminder.title}
                            </Typography>
                            {reminder.isRecurring && (
                              <Chip label="Recurring" size="small" variant="outlined" />
                            )}
                            {status === 'snoozed' && (
                              <Chip label="Snoozed" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {reminder.description && (
                              <Typography variant="body2" color="text.secondary">
                                {reminder.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(reminder.reminderDate).format('MMM DD, YYYY HH:mm')}
                              {reminder.isRecurring && ` • Every ${reminder.recurrenceInterval} ${reminder.recurrenceType}`}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!reminder.isCompleted && status !== 'snoozed' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleComplete(reminder)}
                          >
                            Complete
                          </Button>
                        )}
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget)
                            setSelectedReminder(reminder)
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </ListItem>
                    {index < filteredReminders.length - 1 && <Divider />}
                  </React.Fragment>
                )
              })}
            </List>
          </Paper>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Notifications sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {searchTerm || filterStatus !== 'all' ? 'No reminders found' : 'No reminders yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first reminder to stay on top of your financial tasks'
              }
            </Typography>
            {!searchTerm && filterStatus === 'all' && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setDialogOpen(true)}
              >
                Create Reminder
              </Button>
            )}
          </Paper>
        )}

        {/* Floating Action Button for Mobile */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' }
          }}
          onClick={() => setDialogOpen(true)}
        >
          <Add />
        </Fab>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {selectedReminder && !selectedReminder.isCompleted && (
            <MenuItem onClick={() => selectedReminder && handleComplete(selectedReminder)}>
              <CheckCircle sx={{ mr: 1 }} />
              Complete
            </MenuItem>
          )}
          {selectedReminder && !selectedReminder.isCompleted && getReminderStatus(selectedReminder) !== 'snoozed' && (
            <MenuItem onClick={() => selectedReminder && openSnoozeDialog(selectedReminder)}>
              <Snooze sx={{ mr: 1 }} />
              Snooze
            </MenuItem>
          )}
          <MenuItem onClick={() => selectedReminder && openEditDialog(selectedReminder)}>
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem 
            onClick={() => selectedReminder && openDeleteConfirm(selectedReminder)}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Reminder Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DialogTitle>
              {editingReminder ? 'Edit Reminder' : 'Create Reminder'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Title"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description (Optional)"
                      multiline
                      rows={2}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="reminderDate"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <DateTimePicker
                      label="Reminder Date & Time"
                      value={dayjs(field.value)}
                      onChange={(newValue) => field.onChange(newValue?.toDate())}
                      slotProps={{
                        textField: {
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          fullWidth: true
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name="isRecurring"
                  control={form.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Recurring Reminder"
                    />
                  )}
                />
                {form.watch('isRecurring') && (
                  <>
                    <Controller
                      name="recurrenceType"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Recurrence Type</InputLabel>
                          <Select
                            {...field}
                            label="Recurrence Type"
                          >
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="recurrenceInterval"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Repeat Every"
                          type="number"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || `Repeat every ${field.value} ${form.watch('recurrenceType')}`}
                          fullWidth
                          inputProps={{ min: 1 }}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                {editingReminder ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snooze Dialog */}
        <Dialog open={snoozeDialogOpen} onClose={() => setSnoozeDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Snooze Reminder</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Snooze for (hours)"
                type="number"
                value={snoozeHours}
                onChange={(e) => setSnoozeHours(Number(e.target.value))}
                fullWidth
                inputProps={{ min: 1, max: 168 }}
                helperText="Snooze for 1-168 hours (1 week max)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSnoozeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSnooze} variant="contained">
              Snooze
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <ConfirmDialog
          open={deleteConfirmOpen}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={handleDelete}
          title="Delete Reminder"
          message={`Are you sure you want to delete "${reminderToDelete?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          severity="error"
        />
      </Container>
    </LocalizationProvider>
  )
}

export default RemindersPage